import apiV2 from '../api/v2';
import type { TiptapDoc } from '@odo/shared-ui';
import type {
    VirtualAppointmentStatus,
    VirtualAppointmentTeam,
    VirtualAppointmentTriggerKind
} from '@odo/shared-ui/clinical/virtualAppointments/types';

export {
    VIRTUAL_APPOINTMENT_TEAMS,
    VIRTUAL_APPOINTMENT_TRIGGER_KINDS,
    type VirtualAppointmentStatus,
    type VirtualAppointmentTeam,
    type VirtualAppointmentTriggerKind
} from '@odo/shared-ui/clinical/virtualAppointments/types';

export const PLANNABLE_TRIGGER_KINDS = ['aligner', 'weeks', 'nogos'] as const;
export type PlannableTriggerKind = (typeof PLANNABLE_TRIGGER_KINDS)[number];

// Where week N counts from; 'now' makes the API stamp the save time as the VA's own anchor.
export const VIRTUAL_APPOINTMENT_WEEKS_FROM = ['treatment_start', 'now'] as const;
export type VirtualAppointmentWeeksFrom = (typeof VIRTUAL_APPOINTMENT_WEEKS_FROM)[number];

export {
    MAX_TRIGGER_ALIGNER,
    MAX_TRIGGER_NOGOS,
    MAX_TRIGGER_WEEKS,
    MIN_TRIGGER_ALIGNER,
    MIN_TRIGGER_NOGOS,
    MIN_TRIGGER_WEEKS
} from './virtualAppointmentTriggerLimits';
export const MAX_PLANNED_VIRTUAL_APPOINTMENTS = 20;
export const MAX_CUSTOM_TYPE_LENGTH = 120;
export const MAX_DESCRIPTION_NODES = 500;
export const MAX_HANDOFF_REASON_LENGTH = 2000;

// Wire shape the RDI/SDI submit, edit and draft routes accept. `id` marks an existing VA.
interface VirtualAppointmentPlanBase {
    id?: string;
    typeCode: string | null;
    typeCustom: string | null;
    team: VirtualAppointmentTeam;
    /** A member of `team`; the patient is assigned to them when this VA moves them into its step. */
    assigneeEmail?: string | null;
    descriptionDoc: TiptapDoc | null;
}

export type VirtualAppointmentPlan = VirtualAppointmentPlanBase &
    (
        | {
              triggerKind: 'aligner';
              triggerAligner: number;
              triggerWeeks?: null;
              triggerNogos?: null;
          }
        | {
              triggerKind: 'weeks';
              triggerWeeks: number;
              weeksFrom: VirtualAppointmentWeeksFrom;
              triggerAligner?: null;
              triggerNogos?: null;
          }
        | { triggerKind: 'nogos'; triggerNogos: number; triggerWeeks?: null; triggerAligner?: null }
        // Only ever an existing VA echoed back; the API rejects a new one.
        | { triggerKind: 'now'; triggerWeeks?: null; triggerAligner?: null; triggerNogos?: null }
    );

// Lenient shape drafts persist: rows are saved exactly as typed, complete or not.
export interface VirtualAppointmentDraftPlan {
    id?: string | null;
    typeCode?: string | null;
    typeCustom?: string | null;
    triggerKind?: VirtualAppointmentTriggerKind | null;
    /** Any integer; not necessarily in range. */
    triggerWeeks?: number | null;
    weeksFrom?: VirtualAppointmentWeeksFrom | null;
    /** Only on a saved VA read back from the API: its week anchor, as on the DTO. */
    triggerWeeksFrom?: string | null;
    /** Any integer; not necessarily in range. */
    triggerAligner?: number | null;
    /** Any integer; not necessarily in range. */
    triggerNogos?: number | null;
    team?: VirtualAppointmentTeam | null;
    assigneeEmail?: string | null;
    descriptionDoc?: TiptapDoc | null;
}

// 'staff' is a hand cancel with the author's reason; the other kinds are system cancels.
export type VirtualAppointmentCancellationKind =
    'staff' | 'superseded_by_rdi' | 'document_invalidated';

export interface VirtualAppointmentCancellation {
    kind: VirtualAppointmentCancellationKind;
    reason: string | null;
}

export interface VirtualAppointmentDto {
    id: string;
    patientId: string;
    typeCode: string | null;
    typeCustom: string | null;
    typeLabel: string | null;
    triggerKind: VirtualAppointmentTriggerKind;
    /** Weeks after the Treatment-phase start, or after `triggerWeeksFrom` when set. */
    triggerWeeks: number | null;
    /** The instant (ISO) a "from now" week trigger counts from; null counts from the treatment start. */
    triggerWeeksFrom: string | null;
    triggerAligner: number | null;
    /** Consecutive DM no-gos (go back included) that open the VA. */
    triggerNogos: number | null;
    /** No-gos counted so far toward `triggerNogos`; 0 for other kinds. */
    nogoCount: number;
    /** Where a no-go VA's count starts (ISO); null for other kinds. */
    nogoCountingSince: string | null;
    team: VirtualAppointmentTeam;
    assigneeEmail: string | null;
    description: string | null;
    descriptionDoc: TiptapDoc | null;
    status: VirtualAppointmentStatus;
    /** Anything past 'planned': read-only in the RDI/SDI wizard. */
    frozen: boolean;
    sourceEventGroupId: string | null;
    originEventId: string | null;
    createdByUserId: string;
    createdAt: string;
    updatedAt: string;
    resolvedAt: string | null;
    /** Why a canceled VA was canceled; null otherwise or when unrecorded. */
    cancellation: VirtualAppointmentCancellation | null;
    /** Planned and would open if the trigger ran now. Only the patient list GET sets it; absent reads as false. */
    isDue?: boolean;
}

export interface VirtualAppointmentTypeDto {
    code: string;
    label: string;
    description: string | null;
    sortOrder: number;
}

export interface VirtualAppointmentListFilter {
    groupId?: string;
    status?: VirtualAppointmentStatus;
}

interface ApiEnvelope<T> {
    success: boolean;
    data: T;
}

function patientVirtualAppointmentsPath(patientId: string): string {
    return `/patients/${encodeURIComponent(patientId)}/virtual-appointments`;
}

function virtualAppointmentPath(patientId: string, vaId: string): string {
    return `${patientVirtualAppointmentsPath(patientId)}/${encodeURIComponent(vaId)}`;
}

export async function fetchPatientVirtualAppointments(
    patientId: string,
    filter: VirtualAppointmentListFilter = {}
): Promise<VirtualAppointmentDto[]> {
    const response = await apiV2.get<ApiEnvelope<VirtualAppointmentDto[]>>(
        patientVirtualAppointmentsPath(patientId),
        { params: filter }
    );
    return response.data.data;
}

// One planned VA added outside the RDI/SDI wizard; the route 400s on an id (it means "edit").
export async function createVirtualAppointment(
    patientId: string,
    plan: VirtualAppointmentPlan
): Promise<VirtualAppointmentDto> {
    const { id: _id, ...newPlan } = plan;
    const response = await apiV2.post<ApiEnvelope<VirtualAppointmentDto>>(
        patientVirtualAppointmentsPath(patientId),
        newPlan
    );
    return response.data.data;
}

interface VirtualAppointmentOpenNowContent {
    typeCode: string | null;
    typeCustom: string | null;
    team: VirtualAppointmentTeam;
    assigneeEmail: string | null;
    descriptionDoc: TiptapDoc | null;
}

// Without a trigger the VA opens straight away (stored as 'now'); with one it is only planned.
export type VirtualAppointmentOpenNowPlan = VirtualAppointmentOpenNowContent &
    ({ triggerKind?: undefined } | VirtualAppointmentPlannedTrigger);

export interface VirtualAppointmentTriggerOutcome {
    reason: 'moved' | 'armed-only' | 'nothing-due' | 'ineligible-status';
    fromStatus: string | null;
    toStatus: string | null;
    armed: string[];
    assignedTo: string | null;
}

export interface OpenVirtualAppointmentsNowResult {
    virtualAppointments: VirtualAppointmentDto[];
    /** Null when the VAs were saved but the trigger failed; the next automatic run opens them. */
    outcome: VirtualAppointmentTriggerOutcome | null;
}

export async function openVirtualAppointmentsNow(
    patientId: string,
    plans: VirtualAppointmentOpenNowPlan[]
): Promise<OpenVirtualAppointmentsNowResult> {
    const response = await apiV2.post<ApiEnvelope<OpenVirtualAppointmentsNowResult>>(
        `${patientVirtualAppointmentsPath(patientId)}/open-now`,
        { virtualAppointments: plans }
    );
    return response.data.data;
}

// The statuses staff set by hand; 'open' from 'done' is an undo.
export type ManageableVirtualAppointmentStatus = Exclude<
    VirtualAppointmentStatus,
    'planned' | 'canceled'
>;

export type VirtualAppointmentPlannedTrigger =
    | { triggerKind: 'aligner'; triggerAligner: number }
    | { triggerKind: 'weeks'; triggerWeeks: number; weeksFrom: VirtualAppointmentWeeksFrom }
    | { triggerKind: 'nogos'; triggerNogos: number };

// A no-go threshold is absolute; the count restarts at 0 on snooze.
export type VirtualAppointmentSnoozeTrigger = VirtualAppointmentPlannedTrigger;

export async function updateVirtualAppointmentStatus(
    patientId: string,
    vaId: string,
    status: ManageableVirtualAppointmentStatus
): Promise<VirtualAppointmentDto> {
    const response = await apiV2.patch<ApiEnvelope<VirtualAppointmentDto>>(
        `${virtualAppointmentPath(patientId, vaId)}/status`,
        { status }
    );
    return response.data.data;
}

// Only planned, open and waiting VAs can be canceled, and a canceled VA can't be reopened; 409 otherwise.
export async function cancelVirtualAppointment(
    patientId: string,
    vaId: string,
    reason: string
): Promise<VirtualAppointmentDto> {
    const response = await apiV2.post<ApiEnvelope<VirtualAppointmentDto>>(
        `${virtualAppointmentPath(patientId, vaId)}/cancel`,
        { reason }
    );
    return response.data.data;
}

export async function updateVirtualAppointmentTeam(
    patientId: string,
    vaId: string,
    team: VirtualAppointmentTeam
): Promise<VirtualAppointmentDto> {
    const response = await apiV2.patch<ApiEnvelope<VirtualAppointmentDto>>(
        `${virtualAppointmentPath(patientId, vaId)}/team`,
        { team }
    );
    return response.data.data;
}

export async function snoozeVirtualAppointment(
    patientId: string,
    vaId: string,
    trigger: VirtualAppointmentSnoozeTrigger
): Promise<VirtualAppointmentDto> {
    const response = await apiV2.post<ApiEnvelope<VirtualAppointmentDto>>(
        `${virtualAppointmentPath(patientId, vaId)}/snooze`,
        trigger
    );
    return response.data.data;
}

export const VIRTUAL_APPOINTMENT_HANDOFF_KINDS = ['escalation', 'hand-back'] as const;
export type VirtualAppointmentHandoffKind = (typeof VIRTUAL_APPOINTMENT_HANDOFF_KINDS)[number];

// A manual move between the DM and Clinical VA steps, with the text the author sent along.
export interface VirtualAppointmentHandoffDto {
    kind: VirtualAppointmentHandoffKind;
    reason: string;
    at: string;
    byName: string | null;
    movedCount: number;
}

export interface VirtualAppointmentRoundDto {
    /** When the case entered its current VA episode; null outside one. */
    startedAt: string | null;
    /** This round's manual handoffs, oldest first; absent from older backends. */
    handoffs?: VirtualAppointmentHandoffDto[];
}

export async function fetchVirtualAppointmentRound(
    patientId: string
): Promise<VirtualAppointmentRoundDto> {
    const response = await apiV2.get<ApiEnvelope<VirtualAppointmentRoundDto>>(
        `${patientVirtualAppointmentsPath(patientId)}/round`
    );
    return response.data.data;
}

export async function fetchVirtualAppointmentTypes(): Promise<VirtualAppointmentTypeDto[]> {
    const response = await apiV2.get<ApiEnvelope<VirtualAppointmentTypeDto[]>>(
        '/virtual-appointments/types'
    );
    return response.data.data;
}
